import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/Shared/Button';

interface PaginationProps {
  /** Current page (1-based). */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Total number of entries across all pages (shown in the info label). */
  totalCount: number;
  /** Called with the requested target page. */
  onPageChange: (next: number) => void;
  /** Disables the controls (e.g. while loading). */
  disabled?: boolean;
}

/**
 * Prev/Next pager with a "page X of Y (N entries)" label.
 *
 * Renders nothing when there is only a single page. Reuses the shared
 * `common:pagination.*` translation keys.
 */
export function Pagination({ page, pageCount, totalCount, onPageChange, disabled = false }: PaginationProps) {
  const { t } = useTranslation('common');

  if (pageCount <= 1) return null;

  return (
    <div className="flex items-center justify-between px-1 mt-3">
      <div className="text-[12px] text-slate-500 tabular-nums">
        {t('pagination.info', { page, pageCount, totalCount })}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || disabled}
          title={t('pagination.previous')}
          aria-label={t('pagination.previous')}
          className="p-1.5"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount || disabled}
          title={t('pagination.next')}
          aria-label={t('pagination.next')}
          className="p-1.5"
        >
          <ChevronRight className="w-4 h-4" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}

export default Pagination;
